import { VSecureFileIndex, VStartEncryptResponse } from '@datarock/protobuffers';
import { assign, createMachine, fromPromise } from 'xstate';
import { finishEncryption, startEncryption, validateToken } from './services/encryptionService';
import { useActorRef, useSelector } from '@xstate/react';
import { useCallback, useRef } from 'react';
import encryptionStrings from './constants/encryptionFlowStrings';
import { getCurrentPosition } from './services/gpsService';
// Create the encryption state machine
const encryptionMachine = createMachine({
    id: 'encryption',
    initial: 'idle',
    context: {
        files: [],
        description: '',
        notes: '',
        vaultSettings: {
            allowCopy: true,
            allowPrint: true,
            allowSave: true,
            allowForward: true,
            biometricsRequired: false,
            enableDecryptReceipts: false,
            authorizedUsers: [],
            countries: [],
            sunriseDate: null,
            sunsetDate: null,
            notes: ''
        },
        account: null,
        token: null,
        position: null,
        startResponse: null,
        encryptionResult: null,
        authService: {},
        controller: {},
        platformServices: {},
        error: null,
        loadingStatus: null
    },
    states: {
        idle: {
            on: {
                START_ENCRYPTION: {
                    target: 'validatingToken',
                    actions: assign({
                        files: ({ event }) => event.files,
                        description: ({ event }) => event.description,
                        notes: ({ event }) => event.notes,
                        vaultSettings: ({ event }) => event.vaultSettings,
                        account: ({ event }) => event.account,
                        authService: ({ event }) => event.authService,
                        controller: ({ event }) => event.controller,
                        platformServices: ({ event }) => event.platformServices,
                        error: null,
                        loadingStatus: encryptionStrings.loadingValidatingToken
                    })
                }
            }
        },
        validatingToken: {
            invoke: {
                src: fromPromise(async ({ input }) => {
                    const { account, controller, authService } = input.context;
                    if (!account)
                        throw new Error(encryptionStrings.errorNoAccount);
                    const result = await validateToken({
                        account,
                        controller,
                        authService
                    });
                    return result;
                }),
                input: ({ context }) => ({ context }),
                onDone: [
                    {
                        guard: ({ event }) => 'requiresManualLogin' in event.output,
                        target: 'manualLoginRequired',
                        actions: assign({
                            error: () => new Error('Manual login required'),
                            loadingStatus: null
                        })
                    },
                    {
                        target: 'acquiringGPS',
                        actions: assign({
                            token: ({ event }) => event.output.token,
                            loadingStatus: encryptionStrings.loadingAcquiringGps
                        })
                    }
                ],
                onError: {
                    target: 'error',
                    actions: assign({
                        error: ({ event }) => event.error,
                        loadingStatus: null
                    })
                }
            }
        },
        acquiringGPS: {
            invoke: {
                src: fromPromise(async ({ input }) => {
                    const { platformServices } = input.context;
                    const position = await getCurrentPosition({ platformServices });
                    return { position };
                }),
                input: ({ context }) => ({ context }),
                onDone: {
                    target: 'startingEncryption',
                    actions: assign({
                        position: ({ event }) => event.output.position,
                        loadingStatus: encryptionStrings.loadingStartingEncryption
                    })
                },
                onError: {
                    target: 'error',
                    actions: assign({
                        error: ({ event }) => event.error,
                        loadingStatus: null
                    })
                }
            }
        },
        startingEncryption: {
            invoke: {
                src: fromPromise(async ({ input }) => {
                    const { controller, files, description, notes, token, account } = input.context;
                    const result = await startEncryption({
                        controller,
                        files,
                        description,
                        notes,
                        token,
                        account
                    });
                    return result;
                }),
                input: ({ context }) => ({ context }),
                onDone: [
                    {
                        guard: ({ event }) => 'requiresManualLogin' in event.output,
                        target: 'manualLoginRequired',
                        actions: assign({
                            error: ({ event }) => new Error('Manual login required'),
                            loadingStatus: null
                        })
                    },
                    {
                        target: 'performingEncryption',
                        actions: assign({
                            startResponse: ({ event }) => event.output.startResponse,
                            loadingStatus: encryptionStrings.loadingPerformingEncryption
                        })
                    }
                ],
                onError: {
                    target: 'error',
                    actions: assign({
                        error: ({ event }) => event.error,
                        loadingStatus: null
                    })
                }
            }
        },
        manualLoginRequired: {
            on: {
                AUTHENTICATION_COMPLETE: {
                    target: 'acquiringGPS',
                    actions: assign({
                        error: null,
                        loadingStatus: encryptionStrings.loadingAcquiringGps,
                        token: ({ event }) => event.token || null
                    })
                }
            }
        },
        performingEncryption: {
            invoke: {
                src: fromPromise(async ({ input }) => {
                    const { platformServices, files, description, notes, startResponse, vaultSettings } = input.context;
                    if (!startResponse)
                        throw new Error(encryptionStrings.errorNoStartResponse);
                    // Encode server response
                    const serverResponseBytes = VStartEncryptResponse.encode(startResponse).finish();
                    // Perform platform encryption - each platform handles file operations internally
                    const result = await platformServices.encryptFiles(files, {
                        description,
                        notes,
                        serverResponseBytes
                    });
                    // Decode the secureFileList from Uint8Array to protobuf object
                    let decodedSecureFiles;
                    try {
                        decodedSecureFiles = VSecureFileIndex.decode(result.secureFileList);
                    }
                    catch (error) {
                        console.error('Error decoding secureFileList:', error);
                        throw error;
                    }
                    return {
                        ...result,
                        secureFileList: decodedSecureFiles,
                        fileName: result.fileName // Use fileName from the platform implementation
                    };
                }),
                input: ({ context }) => ({ context }),
                onDone: {
                    target: 'finishingEncryption',
                    actions: assign({
                        encryptionResult: ({ event }) => event.output,
                        loadingStatus: encryptionStrings.loadingFinishingEncryption
                    })
                },
                onError: {
                    target: 'error',
                    actions: assign({
                        error: ({ event }) => event.error,
                        loadingStatus: null
                    })
                }
            }
        },
        finishingEncryption: {
            invoke: {
                src: fromPromise(async ({ input }) => {
                    const { account, controller, startResponse, encryptionResult, vaultSettings, position, token } = input.context;
                    if (!startResponse || !encryptionResult || !position) {
                        throw new Error(encryptionStrings.errorMissingRequiredData);
                    }
                    const result = await finishEncryption({
                        account,
                        controller,
                        startResponse,
                        encryptionResult,
                        vaultSettings,
                        position,
                        fileName: encryptionResult.fileName,
                        token
                    });
                    return result;
                }),
                input: ({ context }) => ({ context }),
                onDone: {
                    target: 'success',
                    actions: assign({
                        loadingStatus: null
                    })
                },
                onError: {
                    target: 'error',
                    actions: assign({
                        error: ({ event }) => event.error,
                        loadingStatus: null
                    })
                }
            }
        },
        success: {
            type: 'final'
        },
        error: {
            on: {
                RESET: {
                    target: 'idle',
                    actions: assign({
                        error: null,
                        loadingStatus: null,
                        files: [],
                        description: '',
                        notes: '',
                        vaultSettings: {
                            allowCopy: true,
                            allowPrint: true,
                            allowSave: true,
                            allowForward: true,
                            biometricsRequired: false,
                            enableDecryptReceipts: false,
                            authorizedUsers: [],
                            countries: [],
                            sunriseDate: null,
                            sunsetDate: null,
                            notes: ''
                        },
                        account: null,
                        token: null,
                        position: null,
                        startResponse: null,
                        encryptionResult: null
                    })
                }
            }
        }
    },
    on: {
        CANCEL_ENCRYPTION: {
            target: '.idle',
            actions: assign({
                error: null,
                loadingStatus: null
            })
        }
    }
});
export function useEncryptionFlow(options) {
    const { account, controller, platformServices, authService, onSuccess, onError } = options;
    // Create the actor ref
    const actorRef = useActorRef(encryptionMachine, {
        input: {
            account,
            controller,
            platformServices,
            authService
        }
    });
    // Selectors for UI state
    const state = useSelector(actorRef, (state) => state);
    const isIdle = useSelector(actorRef, (state) => state.matches('idle'));
    const isValidatingToken = useSelector(actorRef, (state) => state.matches('validatingToken'));
    const isAcquiringGPS = useSelector(actorRef, (state) => state.matches('acquiringGPS'));
    const isStartingEncryption = useSelector(actorRef, (state) => state.matches('startingEncryption'));
    const isPerformingEncryption = useSelector(actorRef, (state) => state.matches('performingEncryption'));
    const isFinishingEncryption = useSelector(actorRef, (state) => state.matches('finishingEncryption'));
    const isSuccess = useSelector(actorRef, (state) => state.matches('success'));
    const isError = useSelector(actorRef, (state) => state.matches('error'));
    const isLoading = useSelector(actorRef, (state) => state.matches('validatingToken') ||
        state.matches('acquiringGPS') ||
        state.matches('startingEncryption') ||
        state.matches('performingEncryption') ||
        state.matches('finishingEncryption'));
    // Context data
    const context = useSelector(actorRef, (state) => state.context);
    const error = useSelector(actorRef, (state) => state.context.error);
    const loadingStatus = useSelector(actorRef, (state) => state.context.loadingStatus);
    const files = useSelector(actorRef, (state) => state.context.files);
    const description = useSelector(actorRef, (state) => state.context.description);
    const notes = useSelector(actorRef, (state) => state.context.notes);
    const vaultSettings = useSelector(actorRef, (state) => state.context.vaultSettings);
    const position = useSelector(actorRef, (state) => state.context.position);
    const startResponse = useSelector(actorRef, (state) => state.context.startResponse);
    const encryptionResult = useSelector(actorRef, (state) => state.context.encryptionResult);
    // Actions
    const startEncryption = useCallback((files, description, notes, vaultSettings) => {
        if (!account) {
            console.warn('Cannot start encryption: no account selected');
            return;
        }
        actorRef.send({
            type: 'START_ENCRYPTION',
            files,
            description,
            notes,
            vaultSettings,
            account,
            authService,
            controller,
            platformServices
        });
    }, [actorRef, account, authService, controller, platformServices]);
    const cancelEncryption = useCallback(() => {
        actorRef.send({ type: 'CANCEL_ENCRYPTION' });
    }, [actorRef]);
    const reset = useCallback(() => {
        actorRef.send({ type: 'RESET' });
    }, [actorRef]);
    const completeAuthentication = useCallback((token) => {
        actorRef.send({ type: 'AUTHENTICATION_COMPLETE', token });
    }, [actorRef]);
    // Handle success and error callbacks
    const successRef = useRef(onSuccess);
    const errorRef = useRef(onError);
    successRef.current = onSuccess;
    errorRef.current = onError;
    // Effect to handle success/error callbacks
    if (isSuccess && encryptionResult && successRef.current) {
        successRef.current(encryptionResult);
    }
    if (isError && error && errorRef.current) {
        errorRef.current(error);
    }
    return {
        // State
        state,
        isIdle,
        isValidatingToken,
        isAcquiringGPS,
        isStartingEncryption,
        isPerformingEncryption,
        isFinishingEncryption,
        isSuccess,
        isError,
        isLoading,
        // Context data
        error,
        loadingStatus,
        files,
        description,
        notes,
        vaultSettings,
        position,
        startResponse,
        encryptionResult,
        // Actions
        startEncryption,
        cancelEncryption,
        reset,
        completeAuthentication
    };
}
